import { produce, type Draft } from 'immer';
import {
  type Duration,
  type Pitch,
  midiOf,
  mod,
  normalizeFifths,
  fingerChord,
  fingerSingle,
  fretOn,
  staffPosition,
  ticks,
  DEFAULT_MAX_FRET,
} from '@simplenotation/theory';
import type { Score, Beat, Note, BeatRef, KeySignature } from '../model/types';
import {
  keySigAt,
  voiceOf,
  isFretted,
  fingeringContextAt,
  soundingMidi,
  middleLineOf,
  clefAt,
  topNote,
  compareRef,
} from '../model/query';

export type Lane = 'staff' | 'tab';

export interface Cursor extends BeatRef {
  lane: Lane;
  /** Tab string under the cursor (1 = highest). */
  string: number;
  /** Diatonic staff position under the cursor (0 = middle line). */
  staffPos: number;
}

export interface BarBeat {
  bar: number;
  beat: number;
}

export interface Selection {
  anchor: BarBeat;
  focus: BarBeat;
  /** Inclusive track range for vertical extension; defaults to the cursor track. */
  trackFrom?: number;
  trackTo?: number;
}

export interface EditorState {
  score: Score;
  cursor: Cursor;
  /** Range selection; null = just the cursor beat. */
  selection: Selection | null;
  /** Current input duration. */
  duration: Duration;
  /** Set by edits that could not be applied (shown as a toast). */
  error?: string;
}

export function initialEditorState(score: Score): EditorState {
  const track = score.tracks[0];
  const lane: Lane = track && track.showStaff ? 'staff' : 'tab';
  const first = voiceOf(score, { track: 0, bar: 0, voice: 0 })[0];
  return {
    score,
    cursor: { track: 0, bar: 0, voice: 0, beat: 0, lane, string: 3, staffPos: 0 },
    selection: null,
    duration: first ? { ...first.duration } : { base: 4, dots: 0 },
  };
}

/** Fifths of the written transposition interval (B♭ instrument → +2, E♭ alto → +3, octave → 0). */
export function transpositionFifths(writtenToSounding: number): number {
  const k = mod(-writtenToSounding * 7, 12);
  return k > 6 ? k - 12 : k;
}

/** Written key for a track at a bar (score key signatures are concert keys). */
export function trackKeyAt(score: Score, track: number, bar: number): KeySignature {
  const ks = keySigAt(score, bar);
  const t = score.tracks[track];
  const shift = t ? transpositionFifths(t.writtenToSounding) : 0;
  if (!shift) return ks;
  return { fifths: normalizeFifths(ks.fifths + shift).fifths, mode: ks.mode };
}

export function middleLineAt(score: Score, track: number, bar: number): Pitch {
  return middleLineOf(clefAt(score.tracks[track], bar));
}

export function staffPosOf(score: Score, ref: Pick<BeatRef, 'track' | 'bar'>, p: Pitch): number {
  return staffPosition(p, middleLineAt(score, ref.track, ref.bar));
}

/** Apply an immer recipe to the score; returns a new state (same object if nothing changed). */
export function mutate(st: EditorState, recipe: (d: Draft<Score>, st: EditorState) => void): EditorState {
  const score = produce(st.score, (d) => {
    recipe(d, st);
  });
  if (score === st.score) return st;
  return { ...st, score, error: undefined };
}

export function withError(st: EditorState, error: string): EditorState {
  return { ...st, error };
}

/** Ordered selection range (or the cursor beat). */
export function selectionRange(st: EditorState): { start: BarBeat; end: BarBeat; trackFrom: number; trackTo: number } {
  const c = st.cursor;
  if (!st.selection) {
    return { start: { bar: c.bar, beat: c.beat }, end: { bar: c.bar, beat: c.beat }, trackFrom: c.track, trackTo: c.track };
  }
  const { anchor, focus } = st.selection;
  const [start, end] = compareRef(anchor, focus) <= 0 ? [anchor, focus] : [focus, anchor];
  const tf = st.selection.trackFrom ?? c.track;
  const tt = st.selection.trackTo ?? c.track;
  return { start, end, trackFrom: Math.min(tf, tt), trackTo: Math.max(tf, tt) };
}

export function hasRange(st: EditorState): boolean {
  if (!st.selection) return false;
  const { anchor, focus } = st.selection;
  return (
    anchor.bar !== focus.bar ||
    anchor.beat !== focus.beat ||
    (st.selection.trackFrom ?? st.cursor.track) !== (st.selection.trackTo ?? st.cursor.track)
  );
}

/** Refs of every selected beat (all selected tracks, cursor voice). */
export function selectedRefs(st: EditorState): BeatRef[] {
  const { start, end, trackFrom, trackTo } = selectionRange(st);
  const out: BeatRef[] = [];
  for (let t = trackFrom; t <= trackTo; t++) {
    for (let bar = start.bar; bar <= end.bar; bar++) {
      const beats = voiceOf(st.score, { track: t, bar, voice: st.cursor.voice });
      const s = bar === start.bar ? start.beat : 0;
      const e = bar === end.bar ? end.beat : beats.length - 1;
      for (let i = s; i <= Math.min(e, beats.length - 1); i++) out.push({ track: t, bar, voice: st.cursor.voice, beat: i });
    }
  }
  return out;
}

export function draftBeat(d: Draft<Score>, ref: BeatRef): Draft<Beat> | undefined {
  return d.tracks[ref.track]?.bars[ref.bar]?.voices[ref.voice]?.[ref.beat];
}

/** Ensure the voice array exists in a draft and return it. */
export function draftVoice(d: Draft<Score>, ref: Pick<BeatRef, 'track' | 'bar' | 'voice'>): Draft<Beat>[] {
  const tb = d.tracks[ref.track].bars[ref.bar];
  while (tb.voices.length <= ref.voice) tb.voices.push([]);
  return tb.voices[ref.voice];
}

/**
 * Re-finger every note of a beat on fretted tracks. Notes that cannot be
 * placed lose their tab position.
 */
export function refingerBeat(d: Draft<Score>, ref: BeatRef): void {
  const track = d.tracks[ref.track];
  if (!isFretted(track as never)) return;
  const beat = draftBeat(d, ref);
  if (!beat || beat.rest || beat.notes.length === 0) return;
  const ctx = fingeringContextAt(d as unknown as Score, ref);
  const midis = beat.notes.map((n) => soundingMidi(n as Note, track as never));
  let res = fingerChord(midis, ctx);
  if (!res) {
    // Place what we can, highest notes first, avoiding used strings.
    const used = new Set<number>();
    res = midis.map(() => null);
    const order = midis.map((m, i) => ({ m, i })).sort((a, b) => b.m - a.m);
    for (const { m, i } of order) {
      const tuning = ctx.tuning.map((t, s) => (used.has(s + 1) ? -1000 : t));
      const p = fingerSingle(m, { ...ctx, tuning });
      if (p) {
        used.add(p.string);
        res[i] = p;
      }
    }
  }
  beat.notes.forEach((n, i) => {
    const p = res![i];
    if (p) {
      n.string = p.string;
      n.fret = p.fret;
    } else {
      delete n.string;
      delete n.fret;
    }
  });
}

/**
 * After a pitch change, keep each note on its string when the new fret is in
 * range and the string is free; otherwise re-finger the whole beat.
 */
export function retabKeepStrings(d: Draft<Score>, ref: BeatRef, maxFret = DEFAULT_MAX_FRET): void {
  const track = d.tracks[ref.track];
  if (!isFretted(track as never)) return;
  const beat = draftBeat(d, ref);
  if (!beat || beat.rest) return;
  const ctx = { tuning: track.tuning ?? [], capo: track.capo ?? 0, maxFret };
  const seen = new Set<number>();
  let ok = true;
  for (const n of beat.notes) {
    if (n.string === undefined || seen.has(n.string)) {
      ok = false;
      break;
    }
    const f = fretOn(soundingMidi(n as Note, track as never), n.string, ctx);
    if (f === null) {
      ok = false;
      break;
    }
    seen.add(n.string);
  }
  if (!ok) {
    refingerBeat(d, ref);
    return;
  }
  for (const n of beat.notes) n.fret = fretOn(soundingMidi(n as Note, track as never), n.string!, ctx)!;
}

/** Notes the cursor points at: the note on the cursor string/staff position, else the whole beat. */
export function cursorNoteIndexes(st: EditorState, beat: Beat): number[] {
  if (beat.rest || beat.notes.length === 0) return [];
  const c = st.cursor;
  if (c.lane === 'tab') {
    const i = beat.notes.findIndex((n) => n.string === c.string);
    if (i >= 0) return [i];
  } else {
    const mid = middleLineAt(st.score, c.track, c.bar);
    const i = beat.notes.findIndex((n) => staffPosition(n.pitch, mid) === c.staffPos);
    if (i >= 0) return [i];
  }
  if (beat.notes.length === 1) return [0];
  return beat.notes.map((_, i) => i);
}

/** Every note targeted by a note-level command: all notes in a range selection, else the cursor note(s). */
export function targetNotes(st: EditorState): { ref: BeatRef; idx: number[] }[] {
  if (hasRange(st)) {
    return selectedRefs(st)
      .map((ref) => {
        const b = voiceOf(st.score, ref)[ref.beat];
        return { ref, idx: b && !b.rest ? b.notes.map((_, i) => i) : [] };
      })
      .filter((x) => x.idx.length > 0);
  }
  const b = voiceOf(st.score, st.cursor)[st.cursor.beat];
  if (!b) return [];
  const idx = cursorNoteIndexes(st, b);
  return idx.length ? [{ ref: { ...st.cursor }, idx }] : [];
}

/** Cursor update after moving onto a beat: duration follows the beat, staff position the top note. */
export function focusBeat(st: EditorState, ref: BeatRef, keepSelection = false): EditorState {
  const beats = voiceOf(st.score, ref);
  const beat = beats[ref.beat];
  const cursor: Cursor = { ...st.cursor, ...ref };
  let duration = st.duration;
  if (beat) {
    duration = { ...beat.duration };
    if (!beat.rest && beat.notes.length && cursor.lane === 'staff') {
      cursor.staffPos = staffPosOf(st.score, ref, topNote(beat).pitch);
    }
  }
  return { ...st, cursor, duration, selection: keepSelection ? st.selection : null, error: undefined };
}

export function beatTicks(b: Beat): number {
  return b.grace ? 0 : ticks(b.duration);
}

export function samePitch(a: Pitch, b: Pitch): boolean {
  return midiOf(a) === midiOf(b) && a.step === b.step;
}
